/**
 * ONE TAP — room screen.
 *
 * Three jobs, in priority order:
 *   1. Get a friend into this room.  The code is the biggest thing on screen and
 *      COPY LINK is one click away with real feedback.
 *   2. Teach the chamber rule.  A player who reaches the first round without
 *      understanding "catch to load" has already lost, so the three rules are
 *      always on screen — as a typographic band, not a fourth white card.
 *   3. Get the match started.  Team columns take a click or a drag, ready is one
 *      button, and START MATCH always says out loud why it is disabled.
 */

import { MAX_PLAYERS_PER_ROOM, TEAM_A, TEAM_B, TEAM_NAMES, TEAM_SIZE_MAX } from '@shared/constants';
import type { LobbyInfo, LobbyPlayerInfo } from '@shared/types';
import { esc, teamColor } from './settings';

export interface LobbyHooks {
  onReady(r: boolean): void;
  onSwitchTeam(t: number): void;
  onStartMatch(): void;
  onSetBots(f: boolean): void;
  onLeave(): void;
  onOpenSettings(): void;
  onUiSound?(kind: 'hover' | 'click' | 'back' | 'error' | 'ready'): void;
}

/** `ParentNode` is ambiguous here — @cloudflare/workers-types declares a global one
 *  for HTMLRewriter — so this takes the structural shape it actually needs. */
type Queryable = { querySelector(selectors: string): Element | null };

const q = <T extends HTMLElement>(root: Queryable, sel: string): T => {
  const found = root.querySelector(sel);
  if (!found) throw new Error(`lobby: missing element ${sel}`);
  return found as T;
};

/** Shareable absolute link that drops a friend straight into the join flow. */
export function roomShareUrl(code: string): string {
  if (typeof location === 'undefined') return code;
  return `${location.origin}${location.pathname}#${code}`;
}

/**
 * The rules band. NOT a card and NOT titled.
 *
 * It used to be a fourth white rounded panel headed HOW TO PLAY, above a second
 * heading reading CONTROLS. Both headings were labels of labels — three lines
 * that read ONE GUN PER TEAM / CATCH A PASS TO RELOAD / HEAD KILLS · BODY
 * WOUNDS do not need to be told they are how to play, and a grid of keycaps does
 * not need to be told it is controls. Stripping the chrome also breaks the stack
 * of identical cards that made this screen read as a template, and buys back the
 * vertical budget START MATCH needs at 1600x900.
 */
const HOW_TO_PLAY = `
<section class="ot-howto" aria-label="Rules and controls">
  <!-- Three lines, no prose. Teach ONLY what is punishing to discover: that the
       gun is shared, and that a catch is the only reload. Everything else — the
       unarmed speed buff, PULSE, floor pickups being empty, SHIFT to refuse a
       heal — is discovery, and explaining it here would rob the player of it.
       See DESIGN.md §8. Resist every urge to add a fourth line. -->
  <ol class="ot-howto__steps">
    <li class="ot-howto__step"><b>ONE GUN PER TEAM</b></li>
    <li class="ot-howto__step ot-howto__step--key"><b>CATCH A PASS TO RELOAD</b></li>
    <li class="ot-howto__step"><b>HEAD KILLS &middot; BODY WOUNDS</b></li>
  </ol>

  <!-- These MUST match DEFAULT_BINDINGS in src/client/input/bindings.ts. There is no
       key for picking a gun up: walking over one takes it (and it is empty — floor
       pickups never chamber), so listing a bind for it would be a lie. -->
  <ul class="ot-controls__grid">
    <li><kbd>W A S D</kbd><span>MOVE</span></li>
    <li><kbd>SPACE</kbd><span>JUMP / DOUBLE JUMP</span></li>
    <li><kbd>CTRL</kbd><span>CROUCH / SLIDE</span></li>
    <li><kbd>SHIFT</kbd><span>DASH &middot; CATCH RAW</span></li>
    <li><kbd>LMB</kbd><span>FIRE</span></li>
    <li><kbd>RMB</kbd><span>THROW</span></li>
    <li><kbd>E</kbd><span>GRAPPLE</span></li>
    <li><kbd>F</kbd><span>PULSE</span></li>
    <li><kbd>TAB</kbd><span>SCOREBOARD</span></li>
    <li><kbd>ESC</kbd><span>SETTINGS</span></li>
  </ul>
</section>`;

const LOBBY_HTML = `
<div class="ot-menu__bg ot-menu__bg--calm" aria-hidden="true">
  <div class="ot-bg__sky"></div>
  <div class="ot-bg__sun"></div>
  <div class="ot-bg__haze"></div>
</div>

<div class="ot-lobby__inner">
  <header class="ot-lobby__head">
    <button class="ot-btn ot-btn--ghost ot-lobby__leave" type="button" id="ot-leave">&larr; LEAVE</button>
    <div class="ot-lobby__brand" aria-hidden="true">ONE<span>TAP</span></div>
    <button class="ot-btn ot-btn--ghost" type="button" id="ot-lobby-settings">SETTINGS</button>
  </header>

  <!-- No panel heading. A four-character code set 43px tall above a button
       reading COPY INVITE LINK does not need to be told it is an invite, and the
       heading was the second place this screen said the same thing. DESIGN.md §8. -->
  <section class="ot-invite ot-panel" aria-label="Room invite">
    <!-- aria-label, not title: a tooltip that says what a button plainly does is
         explanatory copy for sighted players and a hover-delay nobody waits out.
         Screen readers still need the button's purpose named. -->
    <button class="ot-invite__code" type="button" id="ot-code-btn" aria-label="Copy room code">
      <span class="ot-invite__prefix" id="ot-code-prefix">ONETAP-</span>
      <span class="ot-invite__body" id="ot-code-body"></span>
    </button>
    <div class="ot-invite__actions">
      <button class="ot-btn ot-btn--primary ot-invite__copy" type="button" id="ot-copy">
        <span class="ot-invite__copyicon" aria-hidden="true"></span>
        <span id="ot-copy-label">COPY INVITE LINK</span>
      </button>
      <!-- Empty until a copy actually happens. It used to read "Anyone with this
           link drops straight into the room", which is what an invite link is.
           This line reports a result; it does not explain a concept. -->
      <span class="ot-invite__hint" id="ot-copy-status" role="status" aria-live="polite"></span>
    </div>
  </section>

  <section class="ot-teams" aria-label="Teams">
    <div class="ot-team ot-team--a ot-panel" data-team="0">
      <header class="ot-team__head">
        <h3 class="ot-team__name" id="ot-team-a-name"></h3>
        <span class="ot-team__count" id="ot-team-a-count">0</span>
      </header>
      <ul class="ot-team__list" id="ot-team-a-list" aria-labelledby="ot-team-a-name"></ul>
      <button class="ot-team__join" type="button" data-join="0">JOIN THIS TEAM</button>
    </div>

    <div class="ot-teams__vs" aria-hidden="true"><span>VS</span></div>

    <div class="ot-team ot-team--b ot-panel" data-team="1">
      <header class="ot-team__head">
        <h3 class="ot-team__name" id="ot-team-b-name"></h3>
        <span class="ot-team__count" id="ot-team-b-count">0</span>
      </header>
      <ul class="ot-team__list" id="ot-team-b-list" aria-labelledby="ot-team-b-name"></ul>
      <button class="ot-team__join" type="button" data-join="1">JOIN THIS TEAM</button>
    </div>
  </section>

  ${HOW_TO_PLAY}

  <footer class="ot-lobby__bar">
    <label class="ot-switch ot-switch--compact" for="ot-bots">
      <input type="checkbox" id="ot-bots" />
      <span class="ot-switch__track"><span class="ot-switch__thumb"></span></span>
      <!-- The empty-slot list already shows BOT in every slot the moment this is
           on, so the switch does not need a sentence describing itself. The sub
           line only ever carries the host permission, which a non-host is
           punished for not knowing. -->
      <span class="ot-switch__label">FILL WITH BOTS<em id="ot-bots-hint"></em></span>
    </label>

    <div class="ot-lobby__actions">
      <button class="ot-btn ot-btn--ready" type="button" id="ot-ready" aria-pressed="false">
        <span class="ot-btn__check" aria-hidden="true"></span><span id="ot-ready-label">READY UP</span>
      </button>
      <button class="ot-btn ot-btn--hero ot-btn--start" type="button" id="ot-start">START MATCH</button>
    </div>

    <p class="ot-lobby__why" id="ot-why" role="status" aria-live="polite"></p>
  </footer>

  <p class="ot-conn ot-lobby__conn" id="ot-lobby-conn" role="status" aria-live="polite"></p>
</div>`;

interface CardRefs {
  root: HTMLLIElement;
  name: HTMLElement;
  ping: HTMLElement;
  state: HTMLElement;
}

export class LobbyScreen {
  readonly element: HTMLElement;

  private readonly hooks: LobbyHooks;
  private readonly codeBody: HTMLElement;
  private readonly codeBtn: HTMLButtonElement;
  private readonly copyBtn: HTMLButtonElement;
  private readonly copyLabel: HTMLElement;
  private readonly copyStatus: HTMLElement;
  private readonly lists: [HTMLElement, HTMLElement];
  private readonly counts: [HTMLElement, HTMLElement];
  private readonly joinBtns: HTMLButtonElement[];
  private readonly botsToggle: HTMLInputElement;
  private readonly botsHint: HTMLElement;
  private readonly readyBtn: HTMLButtonElement;
  private readonly readyLabel: HTMLElement;
  private readonly startBtn: HTMLButtonElement;
  private readonly why: HTMLElement;
  private readonly conn: HTMLElement;

  private readonly cards = new Map<string, CardRefs>();
  private lobby: LobbyInfo | null = null;
  private localId = '';
  private copyTimer = 0;
  private lastCode = '';

  constructor(hooks: LobbyHooks) {
    this.hooks = hooks;

    const el = document.createElement('section');
    el.className = 'ot-screen ot-lobby';
    el.hidden = true;
    el.innerHTML = LOBBY_HTML;
    this.element = el;

    this.codeBody = q(el, '#ot-code-body');
    this.codeBtn = q<HTMLButtonElement>(el, '#ot-code-btn');
    this.copyBtn = q<HTMLButtonElement>(el, '#ot-copy');
    this.copyLabel = q(el, '#ot-copy-label');
    this.copyStatus = q(el, '#ot-copy-status');
    this.lists = [q(el, '#ot-team-a-list'), q(el, '#ot-team-b-list')];
    this.counts = [q(el, '#ot-team-a-count'), q(el, '#ot-team-b-count')];
    this.joinBtns = Array.from(el.querySelectorAll<HTMLButtonElement>('[data-join]'));
    this.botsToggle = q<HTMLInputElement>(el, '#ot-bots');
    this.botsHint = q(el, '#ot-bots-hint');
    this.readyBtn = q<HTMLButtonElement>(el, '#ot-ready');
    this.readyLabel = q(el, '#ot-ready-label');
    this.startBtn = q<HTMLButtonElement>(el, '#ot-start');
    this.why = q(el, '#ot-why');
    this.conn = q(el, '#ot-lobby-conn');

    q(el, '#ot-team-a-name').textContent = `TEAM ${TEAM_NAMES[TEAM_A]}`;
    q(el, '#ot-team-b-name').textContent = `TEAM ${TEAM_NAMES[TEAM_B]}`;

    this.copyBtn.addEventListener('click', () => void this.copy('link'));
    this.codeBtn.addEventListener('click', () => void this.copy('code'));

    for (const b of this.joinBtns) {
      b.addEventListener('click', () => {
        const team = Number(b.dataset.join);
        if (this.localPlayer()?.team === team) return;
        this.hooks.onUiSound?.('click');
        this.hooks.onSwitchTeam(team);
      });
    }

    this.readyBtn.addEventListener('click', () => {
      const me = this.localPlayer();
      if (!me) return;
      this.hooks.onUiSound?.(me.ready ? 'back' : 'ready');
      this.hooks.onReady(!me.ready);
    });

    this.startBtn.addEventListener('click', () => {
      if (this.startBtn.disabled) return;
      this.hooks.onUiSound?.('click');
      this.hooks.onStartMatch();
    });

    this.botsToggle.addEventListener('change', () => {
      this.hooks.onUiSound?.('click');
      this.hooks.onSetBots(this.botsToggle.checked);
    });

    q<HTMLButtonElement>(el, '#ot-leave').addEventListener('click', () => {
      this.hooks.onUiSound?.('back');
      this.hooks.onLeave();
    });
    q<HTMLButtonElement>(el, '#ot-lobby-settings').addEventListener('click', () => {
      this.hooks.onUiSound?.('click');
      this.hooks.onOpenSettings();
    });

    for (const b of el.querySelectorAll<HTMLElement>('.ot-btn, .ot-team__join')) {
      b.addEventListener('pointerenter', () => this.hooks.onUiSound?.('hover'));
    }

    this.wireDropTargets();
  }

  // -------------------------------------------------------------------------

  show(): void {
    if (!this.element.hidden) return;
    this.element.hidden = false;
    this.element.classList.add('is-entering');
    window.setTimeout(() => this.element.classList.remove('is-entering'), 700);
  }

  hide(): void {
    this.element.hidden = true;
  }

  /** Idempotent — call it on every `lobby` message; it diffs rather than rebuilds. */
  update(lobby: LobbyInfo, localId: string): void {
    this.lobby = lobby;
    this.localId = localId;

    if (lobby.code !== this.lastCode) {
      this.lastCode = lobby.code;
      const dash = lobby.code.lastIndexOf('-');
      const prefix = dash >= 0 ? lobby.code.slice(0, dash + 1) : '';
      const body = dash >= 0 ? lobby.code.slice(dash + 1) : lobby.code;
      q(this.element, '#ot-code-prefix').textContent = prefix;
      this.codeBody.innerHTML = Array.from(body)
        .map((ch, i) => `<i style="--i:${i}">${esc(ch)}</i>`)
        .join('');
    }

    this.renderRoster(lobby, localId);
    this.renderControls(lobby, localId);
  }

  setConnection(state: 'idle' | 'connecting' | 'connected' | 'error' | 'closed', msg?: string): void {
    const label =
      state === 'connecting'
        ? 'RECONNECTING…'
        : state === 'error'
          ? (msg ?? 'CONNECTION LOST')
          : state === 'closed'
            ? (msg ?? 'DISCONNECTED')
            : '';
    this.conn.textContent = label;
    this.conn.dataset.state = state;
    this.conn.classList.toggle('is-visible', label !== '');
  }

  dispose(): void {
    window.clearTimeout(this.copyTimer);
    this.element.remove();
  }

  // -------------------------------------------------------------------------
  // Roster
  // -------------------------------------------------------------------------

  private localPlayer(): LobbyPlayerInfo | null {
    return this.lobby?.players.find((p) => p.id === this.localId) ?? null;
  }

  private renderRoster(lobby: LobbyInfo, localId: string): void {
    const seen = new Set<string>();
    const perTeam: [LobbyPlayerInfo[], LobbyPlayerInfo[]] = [[], []];

    for (const p of lobby.players) {
      seen.add(p.id);
      const idx = p.team === TEAM_B ? 1 : 0;
      perTeam[idx].push(p);
    }

    for (const [id, card] of this.cards) {
      if (!seen.has(id)) {
        card.root.remove();
        this.cards.delete(id);
      }
    }

    const slots = Math.min(Math.max(lobby.teamSize, 1), TEAM_SIZE_MAX);

    for (let team = 0; team < 2; team++) {
      const list = this.lists[team]!;
      const members = perTeam[team]!;
      this.counts[team]!.textContent = `${members.length}/${slots}`;

      members.forEach((p, i) => {
        const card = this.cardFor(p, localId, lobby.host);
        // Re-append only when the DOM order is already wrong — avoids churn.
        const at = list.children[i];
        if (at !== card.root) list.insertBefore(card.root, at ?? null);
      });

      // Trailing empty slots so the columns keep a stable, readable shape.
      const ghosts = list.querySelectorAll('.ot-slot--ghost');
      const want = Math.max(0, slots - members.length);
      for (let g = ghosts.length; g < want; g++) {
        const ghost = document.createElement('li');
        ghost.className = 'ot-slot ot-slot--ghost';
        ghost.innerHTML = `<span>${lobby.fillWithBots ? 'BOT' : 'OPEN SLOT'}</span>`;
        list.appendChild(ghost);
      }
      for (let g = ghosts.length; g > want; g--) ghosts[g - 1]?.remove();
      for (const ghost of list.querySelectorAll<HTMLElement>('.ot-slot--ghost > span')) {
        ghost.textContent = lobby.fillWithBots ? 'BOT' : 'OPEN SLOT';
      }
    }
  }

  private cardFor(p: LobbyPlayerInfo, localId: string, hostId: string): CardRefs {
    let card = this.cards.get(p.id);
    if (!card) {
      const root = document.createElement('li');
      root.className = 'ot-slot';
      root.dataset.id = p.id;
      root.innerHTML = `
        <span class="ot-slot__dot" aria-hidden="true"></span>
        <span class="ot-slot__name"></span>
        <span class="ot-slot__ping"></span>
        <span class="ot-slot__state"></span>`;
      card = {
        root,
        name: root.querySelector('.ot-slot__name') as HTMLElement,
        ping: root.querySelector('.ot-slot__ping') as HTMLElement,
        state: root.querySelector('.ot-slot__state') as HTMLElement,
      };
      this.cards.set(p.id, card);
      this.wireCard(card, p.id);
    }

    const isLocal = p.id === localId;
    card.root.classList.toggle('is-you', isLocal);
    card.root.classList.toggle('is-ready', p.ready);
    card.root.classList.toggle('is-bot', p.bot);
    card.root.classList.toggle('is-host', p.id === hostId);
    card.root.style.setProperty('--slot-team', teamColor(p.team));
    card.root.draggable = isLocal;
    card.root.tabIndex = isLocal ? 0 : -1;
    card.root.setAttribute(
      'aria-label',
      isLocal
        ? `${p.name} — you. Press Enter to switch team.`
        : `${p.name}${p.bot ? ', bot' : ''}${p.ready ? ', ready' : ', not ready'}`,
    );

    const label = p.name + (isLocal ? ' (YOU)' : '');
    if (card.name.textContent !== label) card.name.textContent = label;

    const ping = p.bot ? 'BOT' : `${Math.round(p.latencyMs)}ms`;
    if (card.ping.textContent !== ping) card.ping.textContent = ping;
    card.ping.dataset.q = p.bot ? 'bot' : p.latencyMs < 60 ? 'good' : p.latencyMs < 130 ? 'ok' : 'bad';

    const state = p.bot ? 'AI' : p.ready ? 'READY' : 'WAITING';
    if (card.state.textContent !== state) card.state.textContent = state;

    return card;
  }

  private wireCard(card: CardRefs, id: string): void {
    card.root.addEventListener('dragstart', (ev) => {
      if (id !== this.localId) {
        ev.preventDefault();
        return;
      }
      ev.dataTransfer?.setData('text/plain', id);
      if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
      this.element.classList.add('is-dragging');
    });
    card.root.addEventListener('dragend', () => {
      this.element.classList.remove('is-dragging');
      for (const t of this.element.querySelectorAll('.ot-team')) t.classList.remove('is-dropzone');
    });
    // Keyboard parity for the drag: Enter/Space on your own card swaps sides.
    card.root.addEventListener('keydown', (ev) => {
      if (id !== this.localId) return;
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      ev.preventDefault();
      this.switchLocalTeam();
    });
  }

  private wireDropTargets(): void {
    for (const col of this.element.querySelectorAll<HTMLElement>('.ot-team')) {
      const team = Number(col.dataset.team);
      col.addEventListener('dragover', (ev) => {
        if (this.localPlayer()?.team === team) return;
        ev.preventDefault();
        if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
        col.classList.add('is-dropzone');
      });
      col.addEventListener('dragleave', () => col.classList.remove('is-dropzone'));
      col.addEventListener('drop', (ev) => {
        ev.preventDefault();
        col.classList.remove('is-dropzone');
        const id = ev.dataTransfer?.getData('text/plain');
        if (id !== this.localId) return;
        if (this.localPlayer()?.team === team) return;
        this.hooks.onUiSound?.('click');
        this.hooks.onSwitchTeam(team);
      });
    }
  }

  private switchLocalTeam(): void {
    const me = this.localPlayer();
    if (!me) return;
    this.hooks.onUiSound?.('click');
    this.hooks.onSwitchTeam(me.team === TEAM_A ? TEAM_B : TEAM_A);
  }

  // -------------------------------------------------------------------------
  // Controls + the "why can't I start" copy
  // -------------------------------------------------------------------------

  private renderControls(lobby: LobbyInfo, localId: string): void {
    const me = this.localPlayer();
    const isHost = localId === lobby.host;

    this.readyBtn.disabled = me === null;
    this.readyBtn.classList.toggle('is-on', me?.ready === true);
    this.readyBtn.setAttribute('aria-pressed', me?.ready === true ? 'true' : 'false');
    this.readyLabel.textContent = me?.ready ? 'READY' : 'READY UP';

    for (const b of this.joinBtns) {
      const team = Number(b.dataset.join);
      const mine = me?.team === team;
      b.disabled = mine;
      b.textContent = mine ? 'YOUR TEAM' : 'JOIN THIS TEAM';
    }

    if (this.botsToggle.checked !== lobby.fillWithBots) this.botsToggle.checked = lobby.fillWithBots;
    this.botsToggle.disabled = !isHost;
    this.botsHint.textContent = isHost ? '' : 'HOST ONLY';

    const humans = lobby.players.filter((p) => !p.bot);
    const notReady = humans.filter((p) => !p.ready);
    const teamA = lobby.players.filter((p) => p.team === TEAM_A).length;
    const teamB = lobby.players.filter((p) => p.team === TEAM_B).length;

    // Labels, not sentences — and only ever a reason the button is DISABLED. An
    // enabled, pulsing START MATCH does not need a line of text agreeing with it.
    let reason = '';
    if (!isHost) {
      const host = lobby.players.find((p) => p.id === lobby.host);
      reason = host ? `HOST · ${host.name.toUpperCase()}` : 'NO HOST';
    } else if (notReady.length > 0) {
      reason =
        notReady.length === 1 && notReady[0]
          ? `WAITING ON ${notReady[0].name.toUpperCase()}`
          : `WAITING ON ${notReady.length} PLAYERS`;
    } else if (!lobby.fillWithBots && (teamA === 0 || teamB === 0)) {
      reason = 'EMPTY TEAM';
    } else if (lobby.players.length > MAX_PLAYERS_PER_ROOM) {
      reason = 'ROOM IS FULL';
    }

    const canStart = isHost && reason === '';
    this.startBtn.disabled = !canStart;
    this.startBtn.classList.toggle('is-armed', canStart);
    this.why.textContent = reason;
    this.why.classList.toggle('is-blocked', !canStart);
    this.startBtn.setAttribute('aria-describedby', 'ot-why');
  }

  // -------------------------------------------------------------------------
  // Copy
  // -------------------------------------------------------------------------

  private async copy(kind: 'link' | 'code'): Promise<void> {
    const code = this.lobby?.code ?? '';
    if (!code) return;
    const text = kind === 'link' ? roomShareUrl(code) : code;
    const ok = await writeClipboard(text);

    window.clearTimeout(this.copyTimer);
    this.hooks.onUiSound?.(ok ? 'click' : 'error');

    const target = kind === 'link' ? this.copyBtn : this.codeBtn;
    target.classList.remove('is-copied', 'is-failed');
    void target.offsetWidth;
    target.classList.add(ok ? 'is-copied' : 'is-failed');

    if (kind === 'link') this.copyLabel.textContent = ok ? 'LINK COPIED' : 'PRESS ⌘/CTRL + C';
    this.copyStatus.textContent = ok
      ? kind === 'link'
        ? 'LINK COPIED'
        : 'CODE COPIED'
      : 'CLIPBOARD BLOCKED';
    this.copyStatus.classList.toggle('is-good', ok);

    this.copyTimer = window.setTimeout(() => {
      target.classList.remove('is-copied', 'is-failed');
      this.copyLabel.textContent = 'COPY INVITE LINK';
      this.copyStatus.classList.remove('is-good');
      this.copyStatus.textContent = '';
    }, 2600);
  }
}

/** Clipboard with a synchronous fallback for insecure contexts and old Safari. */
async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the execCommand path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
